import ArrowPathIcon from '@heroicons/react/24/solid/ArrowPathIcon';
import ArrowRightIcon from '@heroicons/react/24/solid/ArrowRightIcon';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CardProps,
    Divider,
    SvgIcon,
} from '@mui/material';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import { Chart } from '@/components/Statistics/Chart';
import { FunctionComponent, useMemo } from 'react';
import { ApexOptions } from 'apexcharts';

const getChartOptions = (theme: Theme) => {
    return {
        chart: {
            background: 'transparent',
            stacked: false,
            toolbar: {
                show: false,
            },
        },
        colors: [
            theme.palette.primary.main,
            alpha(theme.palette.primary.main, 0.25),
        ],
        dataLabels: {
            enabled: false,
        },
        fill: {
            opacity: 1,
            type: 'solid',
        },
        grid: {
            borderColor: theme.palette.divider,
            strokeDashArray: 2,
            xaxis: {
                lines: {
                    show: true,
                },
            },
            yaxis: {
                lines: {
                    show: true,
                },
            },
        },
        legend: {
            show: false,
        },
        plotOptions: {
            bar: {
                columnWidth: '40px',
            },
        },
        stroke: {
            colors: ['transparent'],
            show: true,
            width: 2,
        },
        theme: {
            mode: theme.palette.mode,
        },
        xaxis: {
            axisBorder: {
                color: theme.palette.divider,
                show: true,
            },
            axisTicks: {
                color: theme.palette.divider,
                show: true,
            },
            categories: [
                'Jan',
                'Feb',
                'Mär',
                'Apr',
                'Mai',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec',
            ],
            labels: {
                offsetY: 5,
                style: {
                    colors: theme.palette.text.secondary,
                },
            },
        },
        yaxis: {
            labels: {
                formatter: (value) => (value > 0 ? `${value}K` : `${value}`),
                offsetX: -10,
                style: {
                    colors: theme.palette.text.secondary,
                },
            },
        },
    };
};

type BarDiagrammProps = {
    chartSeries: ApexOptions['series'];
    sx?: CardProps['sx'];
};

export const BarDiagramm: FunctionComponent<BarDiagrammProps> = ({
    chartSeries,
    sx,
}) => {
    const theme = useTheme();
    const chartOptions = useMemo(() => getChartOptions(theme), [chartSeries]);

    return (
        <Card sx={sx}>
            <CardHeader
                action={
                    <Button
                        color='inherit'
                        size='small'
                        startIcon={
                            <SvgIcon fontSize='small'>
                                <ArrowPathIcon />
                            </SvgIcon>
                        }
                    >
                        Sync
                    </Button>
                }
                title='Sales'
            />
            <CardContent>
                <Chart
                    height={350}
                    options={chartOptions}
                    series={chartSeries}
                    type='bar'
                    width='100%'
                />
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'flex-end' }}>
                <Button
                    color='inherit'
                    endIcon={
                        <SvgIcon fontSize='small'>
                            <ArrowRightIcon />
                        </SvgIcon>
                    }
                    size='small'
                >
                    Details
                </Button>
            </CardActions>
        </Card>
    );
};
